import { isEqual } from 'lodash-es';

// ============================================================================
// Types
// ============================================================================

type AnyZodSchema = { _zod: { output: unknown } };
type InferOutput<T extends AnyZodSchema> = T['_zod']['output'];

/** Utility type that flattens nested intersections for better IDE display */
type Prettify<T> = { [K in keyof T]: T[K] } & NonNullable<unknown>;

/**
 * Distributive Prettify — flattens each branch of a discriminated union
 * separately. Used at the public extraction boundary (`InferDataGraph`,
 * `init()` return) to give consumers a flat object per branch for fast
 * property access, without paying the per-chain `Prettify` cost during
 * graph construction.
 *
 * Why distributive: a non-distributive `Prettify<X | Y>` would only project
 * the keys common to X and Y (since `keyof (X | Y) = keyof X & keyof Y`).
 * Distributing gives `Prettify<X> | Prettify<Y>` — what consumers actually
 * want.
 */
type DistribPrettify<T> = T extends unknown ? { [K in keyof T]: T[K] } : never;

/** Merge that distributes over unions in A - preserves discriminated unions and prettifies */
type MergeDistributive<A, B> = A extends unknown ? Prettify<Omit<A, keyof B> & B> : never;

/**
 * Append a fresh key to the Ctx accumulator. Used by `.node()` and `.computed()`
 * where B is always `{ [K]: V }` for a key that does not exist in any branch of A.
 *
 * Why plain intersection (no `Omit`, no `extends unknown`, no `Prettify`):
 * - `Omit` is unnecessary because `.node()`/`.computed()` reject duplicate keys
 *   at runtime.
 * - `extends unknown` distribution is unnecessary because TypeScript already
 *   auto-distributes intersection over unions.
 * - `Prettify` would re-flatten the entire accumulator on every chained call.
 *   With many chained `.node()` calls, the nested mapped-type evaluation eats
 *   the type-instantiation depth budget and trips TS2589 sooner.
 *
 * Trade-off: skipping `Prettify` per-chain would make downstream structural
 * comparisons slower (walking `A & {n1} & {n2} & ... & {n40}` instead of one
 * flat object). This is mitigated by `DistribPrettify` at the `InferDataGraph`
 * extraction boundary — the accumulator stays cheap during construction, and
 * consumers see flat object shapes with fast property access.
 *
 * Net result: full-repo `tsc` check time +44% vs baseline (with this `Prettify`-
 * deferred approach), depth budget +85% (~24 → ~46 post-discriminator chained
 * calls). The TS2589 ceiling is a hard wall — code stops compiling — while the
 * remaining slowdown is soft. See `src/libs/data-graph/__stress__/depth-budget.ts`.
 */
type AppendKey<A, B> = A & B;

/** Empty object type that works correctly with Merge (unlike Record<string, never>) */
type EmptyObject = Record<never, never>;

type NodeCallback = () => void;

type EffectFn<Ctx, ExternalCtx> = (
  ctx: Ctx,
  ext: ExternalCtx,
  set: <K extends keyof Ctx>(key: K, value: Ctx[K]) => void
) => void;

/** Describes how a target key was found in a graph */
export type KeyMatch =
  | { kind: 'direct' }
  | { kind: 'nested'; discriminator: string; branches: BranchKeyInfo[] };

/** Info about a discriminator branch that contains a target key */
export interface BranchKeyInfo {
  /** Branch value (e.g., 'image:create' or 'SD1') */
  value: string;
  /** How the key was found in this branch */
  match: KeyMatch;
}

/** Options for reset method */
export interface ResetOptions<Ctx> {
  /** Keys to preserve (won't be reset) */
  exclude?: (keyof Ctx & string)[];
}

/** Actions available to node factories for updating the graph */
interface GraphActions<Ctx> {
  /** Update one or more node values */
  set: (values: Partial<Ctx>) => Ctx;
  /** Reset all nodes to their default values */
  reset: (options?: ResetOptions<Ctx>) => Ctx;
}

/** Validation error from Zod */
export interface NodeError {
  message: string;
  code: string;
  path?: (string | number)[];
}

/** Snapshot of a single node's state - Meta is strongly typed per-node */
export interface NodeSnapshot<T = unknown, Meta = unknown> {
  value: T;
  meta: Meta | undefined;
  error: NodeError | undefined;
  isComputed: boolean;
  /** Unique key that changes when meta changes - useful for React keys */
  metaKey: number | undefined;
}

/** Node entry info returned in validation result */
export type NodeEntry =
  | { kind: 'node'; key: string; deps: readonly string[] }
  | { kind: 'computed'; key: string; deps: readonly string[] };

/** Result of validation - similar to Zod's safeParse */
export type ValidationResult<T> =
  | { success: true; data: T; nodes: Record<string, NodeEntry> }
  | { success: false; errors: Record<string, NodeError> };

/** Result of partial validation - always returns valid data, omitting failed nodes */
export type PartialValidationResult<T> = {
  data: Partial<T>;
  errors: Record<string, NodeError>;
};

// ============================================================================
// Storage Adapter Types
// ============================================================================

/**
 * Storage adapter interface for persisting graph state.
 */
export interface StorageAdapter<Ctx extends Record<string, unknown> = Record<string, unknown>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attach(graph: DataGraph<Ctx, any, any>): void;
  onSet(values: Partial<Ctx>, ctx: Ctx): void;
  getValues(): Partial<Ctx>;
  onBeforeEvaluate?(): void;
  onInit(): void;
  /** Remove a storage key from both live storage and the in-memory cache */
  removeKey(key: string): void;
}

// ============================================================================
// Discriminator Types
// ============================================================================

// Extract the Ctx type from a DataGraph or a lazy factory that returns one
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InferGraphContext<G> = G extends DataGraph<infer Ctx, any, any, any>
  ? Ctx
  : // eslint-disable-next-line @typescript-eslint/no-explicit-any
  G extends (ctx: any, ext: any) => DataGraph<infer Ctx, any, any, any>
  ? Ctx
  : never;

// Extract the CtxMeta type from a DataGraph
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InferGraphMeta<G> = G extends DataGraph<any, any, infer CtxMeta, any>
  ? CtxMeta
  : // eslint-disable-next-line @typescript-eslint/no-explicit-any
  G extends (ctx: any, ext: any) => DataGraph<any, any, infer CtxMeta, any>
  ? CtxMeta
  : never;

// Extract the CtxValues type from a DataGraph (4th type parameter)
// This is the intersection of all possible value types, including nested discriminators
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InferGraphValues<G> = G extends DataGraph<any, any, any, infer CtxValues>
  ? CtxValues
  : // eslint-disable-next-line @typescript-eslint/no-explicit-any
  G extends (ctx: any, ext: any) => DataGraph<any, any, any, infer CtxValues>
  ? CtxValues
  : never;

// Branch definition - a graph or a lazy factory that creates one
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BranchGraph = DataGraph<any, any, any, any>;

// Lazy branch factory - receives parent context and external context
type LazyBranchFactory<Ctx, ExternalCtx> = (ctx: Ctx, ext: ExternalCtx) => BranchGraph;

// Branch can be either a graph instance or a lazy factory
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BranchDefinition<Ctx = any, ExternalCtx = any> =
  | BranchGraph
  | LazyBranchFactory<Ctx, ExternalCtx>;

// Branches record - values can be graphs or lazy factories
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BranchesRecord<Ctx = any, ExternalCtx = any> = Record<
  string,
  BranchDefinition<Ctx, ExternalCtx>
>;

// ============================================================================
// Grouped Discriminator Types
// ============================================================================

/**
 * A grouped branch definition - multiple discriminator values share one graph.
 * At the type level, this creates ONE union member with a union of string literals.
 */
interface GroupedBranch<
  Values extends readonly string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Graph extends BranchDefinition<any, any>
> {
  /** The discriminator values that map to this graph */
  readonly values: Values;
  /** The graph for these values */
  readonly graph: Graph;
}

/** Array of grouped branches */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GroupedBranchesArray<Ctx = any, ExternalCtx = any> = readonly GroupedBranch<
  readonly string[],
  BranchDefinition<Ctx, ExternalCtx>
>[];

/**
 * Build discriminated union from grouped branches.
 * Each GroupedBranch creates ONE type branch with a union of its values.
 *
 * Example:
 *   GroupedBranch<['txt2img', 'img2img'], ecosystemGraph>
 *   → { workflow: 'txt2img' | 'img2img' } & EcosystemGraphCtx
 *
 * This reduces type complexity from O(n) branches to O(groups) branches.
 */
// Perf notes:
// - `OmitDistributive<ParentCtx, DiscKey>` is hoisted into a wrapper alias so
//   it is instantiated once per discriminator instead of once per group.
// - The branch fold uses a mapped type over the tuple (`{ [I in keyof Groups]: ... }[number]`)
//   instead of head-tail recursion. This is constant-depth regardless of how many
//   groups exist, which preserves the type-instantiation depth budget for trailing
//   `.node()` chains. Recursion-based folds added one depth level per group; with
//   25 ecosystem groups that consumed 25 depth units before any trailing node chain
//   even started.
type BuildGroupedDiscriminatedUnion<
  ParentCtx extends Record<string, unknown>,
  DiscKey extends string,
  Groups extends GroupedBranchesArray
> = _BuildGroupedDiscWithCachedParent<OmitDistributive<ParentCtx, DiscKey>, DiscKey, Groups>;

type _BuildGroupedDiscWithCachedParent<
  OmittedParent,
  DiscKey extends string,
  Groups extends GroupedBranchesArray
> = {
  [I in keyof Groups]: Groups[I] extends GroupedBranch<infer Values, infer Graph>
    ? MergePreferRight<
        OmittedParent,
        MergePreferRight<
          { [K in DiscKey]: Values[number] },
          OmitDistributive<InferGraphContext<Graph>, DiscKey>
        >
      >
    : never;
}[number];

/**
 * Lazy evaluation of grouped branch metas via mapped type.
 * TypeScript defers evaluation until property is accessed.
 */
type LazyGroupedBranchMetas<Groups extends GroupedBranchesArray> = {
  [K in keyof Groups]: Groups[K] extends GroupedBranch<readonly string[], infer Graph>
    ? InferGraphMeta<Graph>
    : never;
};

/**
 * Lazy evaluation of grouped branch values via mapped type.
 */
type LazyGroupedBranchValues<Groups extends GroupedBranchesArray> = {
  [K in keyof Groups]: Groups[K] extends GroupedBranch<readonly string[], infer Graph>
    ? InferGraphValues<Graph>
    : never;
};

/**
 * Build meta union from grouped branches.
 * Uses lazy evaluation via mapped type for better TypeScript performance.
 */
type BuildGroupedMetaUnion<
  ParentCtxMeta extends Record<string, unknown>,
  Groups extends GroupedBranchesArray
> = ParentCtxMeta & FlattenUnion<LazyGroupedBranchMetas<Groups>[keyof Groups & number]>;

/**
 * Build values union from grouped branches.
 * Uses lazy evaluation via mapped type for better TypeScript performance.
 */
type BuildGroupedValuesUnion<
  ParentCtx extends Record<string, unknown>,
  Groups extends GroupedBranchesArray
> = ParentCtx & FlattenUnion<LazyGroupedBranchValues<Groups>[keyof Groups & number]>;

// Helper: Distributive Omit that preserves discriminated unions
type OmitDistributive<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

// Helper: Merge A and B, where B's types take precedence for overlapping keys
// Distributes over both A and B to preserve discriminated unions from either side.
// This is critical for nested discriminators: the parent's discriminated union (A)
// must be preserved when merging with branch shapes (B).
type MergePreferRight<A, B> = A extends unknown
  ? B extends unknown
    ? Prettify<Omit<A, keyof B> & B>
    : never
  : never;

// Helper: Get the branch shape - combines the discriminator literal with subgraph context
// The subgraph's InferGraphContext includes its nested discriminated unions.
// We omit the discriminator key from the subgraph since we set it to the literal BranchName.
type BranchShape<
  _ParentCtx extends Record<string, unknown>,
  DiscKey extends string,
  BranchName extends string,
  BranchGraph
> = MergePreferRight<
  { [K in DiscKey]: BranchName },
  OmitDistributive<InferGraphContext<BranchGraph>, DiscKey>
>;

// Build the discriminated union for Ctx
// Bottom-up approach: each branch produces its full shape (including nested discriminated unions),
// then we merge with parent context using MergePreferRight.
//
// MergePreferRight distributes over both sides, so:
// - Parent's discriminated union (from earlier discriminators) is preserved
// - Branch's discriminated union (from nested discriminators) is preserved
// This allows type narrowing to work for nested discriminators like: workflow -> input -> images
//
// Perf note: `OmitDistributive<ParentCtx, DiscKey>` is hoisted into a wrapper alias
// so it is instantiated once per discriminator instead of once per branch.
// Without this hoist, a graph with N branches re-evaluates the parent omit N times.
type BuildDiscriminatedUnion<
  ParentCtx extends Record<string, unknown>,
  DiscKey extends string,
  Branches extends BranchesRecord
> = _BuildDiscWithCachedParent<OmitDistributive<ParentCtx, DiscKey>, DiscKey, Branches>;

type _BuildDiscWithCachedParent<
  OmittedParent,
  DiscKey extends string,
  Branches extends BranchesRecord
> = {
  // Map each branch name to its complete shape (merged with parent, subgraph types preferred)
  [BranchName in keyof Branches & string]: MergePreferRight<
    OmittedParent,
    BranchShape<Record<string, unknown>, DiscKey, BranchName, Branches[BranchName]>
  >;
}[keyof Branches & string];

/**
 * Lazy branch type maps - TypeScript defers evaluation of mapped type
 * properties until they are directly accessed.
 *
 * By using a mapped type over branches, TypeScript only evaluates each branch's
 * types when that specific property is accessed, rather than eagerly computing
 * all branches when the type is first encountered.
 *
 * @see https://trpc.io/blog/typescript-performance-lessons
 */
type LazyBranchMetas<Branches extends BranchesRecord> = {
  [K in keyof Branches]: InferGraphMeta<Branches[K]>;
};

type LazyBranchValues<Branches extends BranchesRecord> = {
  [K in keyof Branches]: InferGraphValues<Branches[K]>;
};

/**
 * Flatten a union of objects into a single object type.
 * Creates a type where each key maps to a union of all possible values
 * for that key across all branches.
 *
 * This is more TypeScript-friendly than intersection for Controller lookups because:
 * 1. It doesn't force eager evaluation of all branch types
 * 2. The resulting type is simpler (union of values vs intersection of objects)
 *
 * Example:
 *   FlattenUnion<{ a: string } | { a: number, b: boolean }>
 *   = { a: string | number; b: boolean | never }
 */
type FlattenUnion<T> = {
  [K in T extends unknown ? keyof T : never]: T extends unknown
    ? K extends keyof T
      ? T[K]
      : never
    : never;
};

/**
 * Build the CtxMeta type by combining all branch metas.
 *
 * Uses FlattenUnion for better TypeScript performance with large graphs.
 * Each key maps to a union of all possible meta types for that node.
 *
 * Note: We avoid Prettify here to reduce type evaluation overhead.
 */
type BuildDiscriminatedMetaUnion<
  ParentCtxMeta extends Record<string, unknown>,
  Branches extends BranchesRecord
> = ParentCtxMeta & FlattenUnion<LazyBranchMetas<Branches>[keyof Branches]>;

/**
 * Build the CtxValues type by combining all branch values.
 * This provides a flat record of all possible node keys and their value types.
 *
 * Uses FlattenUnion for better TypeScript performance with large graphs.
 * Note: We avoid Prettify here to reduce type evaluation overhead.
 */
type BuildDiscriminatedValuesUnion<
  ParentCtx extends Record<string, unknown>,
  Branches extends BranchesRecord
> = ParentCtx & FlattenUnion<LazyBranchValues<Branches>[keyof Branches]>;

// ============================================================================
// Graph Entry Types (Runtime)
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DiscriminatorFactory = (ctx: any, ext: any) => BranchesRecord;

type GraphEntry =
  | {
      kind: 'node';
      key: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      factory: (ctx: any, ext: any, actions: any) => any;
      deps: readonly string[];
    }
  | {
      kind: 'computed';
      key: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      compute: (ctx: any, ext: any) => any;
      deps: readonly string[];
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { kind: 'effect'; run: (ctx: any, ext: any, set: any) => void; deps: readonly string[] }
  | {
      kind: 'discriminator';
      discriminatorKey: string;
      factory: DiscriminatorFactory;
      /** Maps individual values to their group name (for groupedDiscriminator).
       *  When present, branch identity uses the group name so that switching
       *  between values in the same group does NOT trigger deactivation/reactivation. */
      groupOf?: Map<string, string>;
    };

// ============================================================================
// Active Entry Types (Runtime - entries with source tracking)
// ============================================================================

/** An entry that is currently active in the graph, with source tracking */
type ActiveEntry = GraphEntry & {
  /** The discriminator path that activated this entry (e.g., 'workflow:txt2img/modelFamily:flux') */
  source: string;
};

/** Tracks an active discriminator branch */
interface ActiveBranch {
  branch: string;
  /** Keys of entries added by this branch (for cleanup) */
  entryKeys: Set<string>;
}

// ============================================================================
// DataGraph Class
// ============================================================================

/**
 * A reactive data graph with type-safe discriminated unions and per-node typed meta.
 *
 * Architecture:
 * - Root graph maintains all state (_ctx, nodeDefs, nodeMeta, activeEntries)
 * - Subgraphs are templates that provide entry definitions
 * - When a discriminator activates, subgraph entries are added to root's activeEntries
 * - When a discriminator deactivates, those entries are removed
 * - Single evaluation loop processes all activeEntries on the root graph
 *
 * @template Ctx - The context type containing all node values (discriminated union)
 * @template ExternalCtx - External context passed from outside the graph
 * @template CtxMeta - Per-node meta type mapping (intersection of all branch metas)
 * @template CtxValues - All possible value types (intersection of all branch contexts)
 */
export type ValueProvider<Ctx> = (key: keyof Ctx & string, ctx: Ctx) => unknown | undefined;

export class DataGraph<
  Ctx extends Record<string, unknown> = EmptyObject,
  ExternalCtx extends Record<string, unknown> = EmptyObject,
  CtxMeta extends Record<string, unknown> = EmptyObject,
  CtxValues extends Record<string, unknown> = Ctx
> {
  /** Timestamp when this graph instance was created (useful for HMR debugging) */
  readonly createdAt = Date.now();

  /** Template entries defined on this graph (used when this graph is a subgraph template) */
  private entries: GraphEntry[] = [];

  /** Active entries currently being evaluated (only on root graph) */
  private activeEntries: ActiveEntry[] = [];

  /** Node definitions (schemas, defaults, meta) - only on root graph */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private nodeDefs = new Map<string, any>();

  /** Node meta values - only on root graph */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private nodeMeta = new Map<string, any>();

  /** Validation errors - only on root graph */
  private nodeErrors = new Map<string, NodeError>();

  /** Node watchers for subscriptions - only on root graph */
  private nodeWatchers = new Map<string, Set<NodeCallback>>();

  /** The context containing all node values - only on root graph */
  private _ctx: Ctx = {} as Ctx;

  /** External context passed from outside */
  private _ext: ExternalCtx = {} as ExternalCtx;

  /** Whether init() has been called */
  private _initialized = false;

  /** Debug mode flag */
  private _debug = false;

  /** Tracks which discriminator branches are currently active.
   *  Keyed by `source|discriminatorKey` to support nested discriminators
   *  that use the same key (e.g., parent and child both discriminating on 'ecosystem'). */
  private activeDiscriminators = new Map<string, ActiveBranch>();

  /** Set of computed node keys (for isComputed check) */
  private computedNodes = new Set<string>();

  /** Value provider for loading values (e.g., from storage) */
  private valueProvider?: ValueProvider<Ctx>;

  /** Cache for lazy branch factories */
  private lazyBranchCache = new Map<string, BranchGraph>();

  /** Storage adapter for persistence */
  private storageAdapter?: StorageAdapter<Ctx>;

  /** Snapshot cache for getSnapshot(key) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private snapshotCache = new Map<string, NodeSnapshot<unknown, any>>();

  /** Cached full context snapshot for getSnapshot() - invalidated on changes */
  private _ctxSnapshot: Ctx | null = null;

  /** Cached full output snapshot for getOutputSnapshot() - invalidated on changes */
  private _outputSnapshot: Ctx | null = null;

  /** Meta keys for tracking meta changes */
  private nodeMetaKeys = new Map<string, number>();

  /** Global watchers for any change */
  private globalWatchers = new Set<() => void>();

  /** Scope dependencies for storage scoping */
  private scopeDependencies = new Map<string, Set<string>>();

  /** Reference to root graph (self-reference on root, parent reference on subgraphs) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rootGraph: DataGraph<any, any, any, any> = this;

  get ctx(): Ctx {
    return this.rootGraph._ctx as Ctx;
  }

  get ext(): ExternalCtx {
    return this.rootGraph._ext as ExternalCtx;
  }

  private addEntry(entry: GraphEntry): void {
    this.entries.push(entry);
    if (entry.kind === 'computed') {
      this.computedNodes.add(entry.key);
    }
  }

  /** Check if a key is a computed value */
  isComputed(key: keyof Ctx & string): boolean {
    return this.rootGraph.computedNodes.has(key);
  }

  /** Check if a node key currently exists in the context */
  hasNode(key: string): boolean {
    return key in this.rootGraph._ctx;
  }

  /**
   * Get meta for a specific node - strongly typed per-node.
   * Returns the exact meta type that was defined for this node.
   */
  getNodeMeta<K extends keyof CtxMeta & string>(key: K): CtxMeta[K] | undefined {
    return this.rootGraph.nodeMeta.get(key);
  }

  /** Get all current node meta */
  getAllMeta(): Partial<CtxMeta> {
    const result: Partial<CtxMeta> = {};
    this.rootGraph.nodeMeta.forEach((meta, key) => {
      (result as Record<string, unknown>)[key] = meta;
    });
    return result;
  }

  /** Get validation error for a specific node */
  getNodeError(key: keyof Ctx & string): NodeError | undefined {
    return this.rootGraph.nodeErrors.get(key);
  }

  /** Get all current validation errors */
  getErrors(): Record<string, NodeError | undefined> {
    const result: Record<string, NodeError | undefined> = {};
    for (const key of this.getNodeKeys()) {
      result[key] = this.rootGraph.nodeErrors.get(key);
    }
    return result;
  }

  /**
   * Validate against output schemas.
   *
   * Two modes:
   * 1. `validate()` — validates internal graph state, updates nodeErrors, notifies watchers
   * 2. `validate(data)` — validates external data against the graph's output schemas
   *    without touching internal state. Useful for cost estimation where you want
   *    to inject defaults (e.g. a placeholder prompt) without mutating the graph.
   *
   * @example
   * ```typescript
   * // Validate internal state (updates nodeErrors, notifies watchers)
   * const result = graph.validate();
   *
   * // Validate external data with overrides
   * const snapshot = graph.getSnapshot();
   * const result = graph.validate({ ...snapshot, prompt: snapshot.prompt ?? 'default' });
   * ```
   */
  validate(data: Record<string, unknown>): ValidationResult<Ctx>;
  validate(): ValidationResult<Ctx>;
  validate(data?: Record<string, unknown>): ValidationResult<Ctx> {
    const root = this.rootGraph;
    const { errors, data: validated } = root._validate(data);

    // Save error state when validating internal data.
    // We intentionally do NOT write output-parsed values back to ctx here.
    // Output schemas may strip extra fields (e.g., enriched resource data added
    // by hydration) — writing those stripped values back would cause unnecessary
    // refetches. The validated (stripped) data is still returned in result.data
    // for submission, while ctx retains the full enriched values for UI/storage.
    if (!data) {
      const changed = root._saveErrors(errors);
      this._notifyChanges(changed);
    }

    return root._buildValidationResult(errors, validated as Ctx);
  }

  /**
   * Validate current graph state, returning as much valid data as possible.
   * Nodes that fail validation are omitted from `data` and reported in `errors`.
   * Always returns a result (never fails entirely), unlike `validate()`.
   *
   * Useful for cost estimation (whatIf) where the backend provides defaults
   * for missing fields like prompt.
   */
  validatePartial(): PartialValidationResult<Ctx> {
    const root = this.rootGraph;
    const { errors, data: validated } = root._validate();

    // Build partial data excluding failed nodes
    const partial: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(validated)) {
      if (!errors.has(key)) partial[key] = value;
    }

    const errorsObj: Record<string, NodeError> = {};
    for (const [key, error] of errors) {
      errorsObj[key] = error;
    }

    return { data: partial as Partial<Ctx>, errors: errorsObj };
  }

  /** Build a ValidationResult from an error map (defaults to nodeErrors) */
  private _buildValidationResult(
    errorMap?: Map<string, NodeError>,
    data?: Ctx
  ): ValidationResult<Ctx> {
    const errors$ = errorMap ?? this.nodeErrors;
    const source = data ?? this._ctx;

    const errors: Record<string, NodeError> = {};
    const nodes: Record<string, NodeEntry> = {};

    for (const entry of this.activeEntries) {
      if (entry.kind === 'node') {
        if (!(entry.key in source)) continue;

        const error = errors$.get(entry.key);
        if (error) {
          errors[entry.key] = error;
        }
        nodes[entry.key] = { kind: 'node', key: entry.key, deps: entry.deps };
      } else if (entry.kind === 'computed') {
        nodes[entry.key] = { kind: 'computed', key: entry.key, deps: entry.deps };
      }
    }

    if (Object.keys(errors).length === 0) {
      return { success: true, data: source, nodes };
    }

    return { success: false, errors };
  }

  /**
   * Validate all active nodes by parsing values through their output schemas.
   * Returns errors and the validated data (with output-schema-parsed values).
   */
  private _validate(source?: Record<string, unknown>): {
    errors: Map<string, NodeError>;
    data: Record<string, unknown>;
  } {
    const ctx = source ?? (this._ctx as Record<string, unknown>);
    const errors = new Map<string, NodeError>();
    const data: Record<string, unknown> = {};

    for (const entry of this.activeEntries) {
      if (entry.kind === 'computed') {
        if (entry.key in ctx) data[entry.key] = ctx[entry.key];
        continue;
      }
      if (entry.kind !== 'node') continue;
      if (!(entry.key in ctx)) continue;

      const def = this.nodeDefs.get(entry.key);
      if (!def?.output) {
        data[entry.key] = ctx[entry.key];
        continue;
      }

      const result = def.output.safeParse(ctx[entry.key]);
      data[entry.key] = result.success ? result.data : ctx[entry.key];

      if (!result.success) {
        const issue = result.error.issues[0];
        errors.set(entry.key, {
          message: issue?.message ?? 'Validation failed',
          code: issue?.code ?? 'unknown',
        });
      }
    }

    return { errors, data };
  }

  /** Save validation errors to nodeErrors state. Returns keys where error state changed. */
  private _saveErrors(errors: Map<string, NodeError>): Set<string> {
    const changed = new Set<string>();
    for (const entry of this.activeEntries) {
      if (entry.kind !== 'node') continue;
      if (!(entry.key in this._ctx)) continue;

      const error = errors.get(entry.key);
      const hadError = this.nodeErrors.has(entry.key);

      if (error) {
        this.nodeErrors.set(entry.key, error);
        if (!hadError) changed.add(entry.key);
      } else if (hadError) {
        this.nodeErrors.delete(entry.key);
        changed.add(entry.key);
      }
    }
    return changed;
  }

  /** Update meta for all active nodes and notify watchers. */
  private _updateAllMeta(): void {
    for (const entry of this.activeEntries) {
      if (entry.kind !== 'node') continue;
      // Skip nodes with when=false (not in ctx)
      if (!(entry.key in this._ctx)) continue;

      const def = this.nodeDefs.get(entry.key);
      if (!def) continue;

      const nodeValue = this._ctx[entry.key as keyof Ctx];

      // Compute meta (static or dynamic with value)
      const metaValue =
        typeof def.meta === 'function' ? def.meta(this._ctx, this._ext, nodeValue) : def.meta;

      this.updateMeta(entry.key, metaValue);
    }
  }

  /**
   * Validate input against output schemas without mutating the original graph.
   * Creates an isolated clone, sets up state, evaluates, and validates.
   *
   * Use this for server-side validation where the template graph should remain unchanged.
   *
   * @param input - Values to validate
   * @param externalCtx - External context for node factories
   * @returns ValidationResult with either validated data or errors
   *
   * @example
   * ```typescript
   * // Server-side validation
   * const result = generationGraph.safeParse(userInput, { session });
   * if (result.success) {
   *   // result.data contains validated values
   * } else {
   *   // result.errors contains validation errors
   * }
   * ```
   */
  safeParse(
    input: Partial<Ctx> = {},
    externalCtx: ExternalCtx = {} as ExternalCtx
  ): ValidationResult<Ctx> {
    const clone = this.clone();
    clone._setup(externalCtx);
    clone._evaluate(input);

    const { errors, data } = clone._validate();
    return clone._buildValidationResult(errors, data as Ctx);
  }

  private watchNode(key: string, callback: NodeCallback): () => void {
    const root = this.rootGraph;
    let callbacks = root.nodeWatchers.get(key);
    if (!callbacks) {
      callbacks = new Set();
      root.nodeWatchers.set(key, callbacks);
    }
    callbacks.add(callback);
    return () => {
      callbacks!.delete(callback);
      if (callbacks!.size === 0) {
        root.nodeWatchers.delete(key);
      }
    };
  }

  private notifyNodeWatchers(key: string) {
    const callbacks = this.rootGraph.nodeWatchers.get(key);
    if (callbacks) {
      for (const callback of callbacks) {
        callback();
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateMeta(key: string, newMeta: any | undefined) {
    const root = this.rootGraph;
    const oldMeta = root.nodeMeta.get(key);
    if (!isEqual(oldMeta, newMeta)) {
      if (newMeta === undefined) {
        root.nodeMeta.delete(key);
        root.nodeMetaKeys.delete(key);
      } else {
        root.nodeMeta.set(key, newMeta);
        root.nodeMetaKeys.set(key, Date.now());
      }
      root.notifyNodeWatchers(key);
    }
  }

  /** Get the unique meta key for a node (changes when meta changes) */
  getNodeMetaKey(key: keyof Ctx & string): number | undefined {
    return this.rootGraph.nodeMetaKeys.get(key);
  }

  /** Subscribe to graph changes */
  subscribe(callback: () => void): () => void;
  subscribe<K extends keyof Ctx & string>(key: K, callback: () => void): () => void;
  subscribe(keyOrCallback: string | (() => void), callback?: () => void): () => void {
    if (typeof keyOrCallback === 'function') {
      this.rootGraph.globalWatchers.add(keyOrCallback);
      return () => {
        this.rootGraph.globalWatchers.delete(keyOrCallback);
      };
    }
    return this.watchNode(keyOrCallback, callback!);
  }

  /** Get a snapshot of graph state */
  getSnapshot(): Ctx;
  getSnapshot<K extends keyof Ctx & string>(
    key: K
  ): NodeSnapshot<Ctx[K], K extends keyof CtxMeta ? CtxMeta[K] : unknown>;
  getSnapshot<K extends keyof Ctx & string>(
    key?: K
  ): Ctx | NodeSnapshot<Ctx[K], K extends keyof CtxMeta ? CtxMeta[K] : unknown> {
    const root = this.rootGraph;
    if (key === undefined) {
      // Return cached snapshot, or create new one if invalidated
      // This ensures stable references when nothing changed (framework-agnostic)
      if (root._ctxSnapshot === null) {
        root._ctxSnapshot = { ...root._ctx } as Ctx;
      }
      return root._ctxSnapshot;
    }

    const value = (root._ctx as Ctx)[key];
    const meta = root.nodeMeta.get(key);
    const error = root.nodeErrors.get(key);
    const isComputed = root.computedNodes.has(key);
    const metaKey = root.nodeMetaKeys.get(key);

    const cached = root.snapshotCache.get(key);
    if (
      cached &&
      Object.is(cached.value, value) &&
      Object.is(cached.meta, meta) &&
      Object.is(cached.error, error) &&
      cached.isComputed === isComputed &&
      cached.metaKey === metaKey
    ) {
      return cached as NodeSnapshot<Ctx[K], K extends keyof CtxMeta ? CtxMeta[K] : unknown>;
    }

    const snapshot: NodeSnapshot<Ctx[K], K extends keyof CtxMeta ? CtxMeta[K] : unknown> = {
      value,
      meta,
      error,
      isComputed,
      metaKey,
    };
    root.snapshotCache.set(key, snapshot as NodeSnapshot<unknown, unknown>);
    return snapshot;
  }

  /**
   * Get a snapshot with all values parsed through their output schemas.
   * Useful for sending minimal, schema-conforming data to the server.
   */
  getOutputSnapshot(): Ctx {
    const root = this.rootGraph;

    if (root._outputSnapshot !== null) {
      return root._outputSnapshot;
    }

    const { data } = root._validate();
    root._outputSnapshot = data as Ctx;
    return root._outputSnapshot;
  }

  setValueProvider(provider: ValueProvider<Ctx> | undefined): void {
    this.rootGraph.valueProvider = provider;
  }

  /**
   * Register a scope dependency: when scopeKey changes, dependentKey should be re-evaluated.
   * This is used by storage adapters to ensure scoped values are reloaded from storage
   * when their scope changes.
   */
  addScopeDependency(scopeKey: string, dependentKey: string): void {
    const root = this.rootGraph;
    let deps = root.scopeDependencies.get(scopeKey);
    if (!deps) {
      deps = new Set();
      root.scopeDependencies.set(scopeKey, deps);
    }
    deps.add(dependentKey);
  }

  /**
   * Get all keys that depend on a given scope key.
   */
  getScopeDependencies(scopeKey: string): string[] {
    return Array.from(this.rootGraph.scopeDependencies.get(scopeKey) ?? []);
  }

  useStorage(adapter: StorageAdapter<Ctx>): this {
    this.storageAdapter = adapter;
    adapter.attach(this);
    return this;
  }

  getStorageAdapter(): StorageAdapter<Ctx> | undefined {
    return this.storageAdapter;
  }

  /**
   * Add a node with a static definition (always present).
   * Meta type is inferred from the meta value provided.
   */
  node<const K extends string, T extends AnyZodSchema, M>(
    key: K,
    def: {
      input?: AnyZodSchema;
      output: T;
      defaultValue?: InferOutput<T>;
      /** Static meta object, or function that computes meta from context and current value */
      meta?: M | ((ctx: Ctx, ext: ExternalCtx, value: InferOutput<T>) => M);
      /** Transform value when dependencies change. Runs after parsing input, before setting value. */
      transform?: (value: InferOutput<T>, ctx: Ctx, ext: ExternalCtx) => InferOutput<T>;
    }
  ): DataGraph<
    AppendKey<Ctx, { [P in K]: InferOutput<T> }>,
    ExternalCtx,
    M extends undefined ? CtxMeta : Prettify<CtxMeta & { [P in K]: M }>,
    Prettify<CtxValues & { [P in K]: InferOutput<T> }>
  >;

  /**
   * Add a node with a factory function (no `when` - always present).
   * Meta type is inferred from what the factory returns.
   */
  node<const K extends string, const Deps extends readonly string[], T extends AnyZodSchema, M>(
    key: K,
    factory: (
      ctx: Ctx,
      ext: ExternalCtx,
      actions: GraphActions<Ctx>
    ) => {
      input?: AnyZodSchema;
      output: T;
      defaultValue?: InferOutput<T>;
      /** Static meta object, or function that computes meta from context and current value */
      meta?: M | ((ctx: Ctx, ext: ExternalCtx, value: InferOutput<T>) => M);
      when?: undefined | true;
      /** Transform value when dependencies change. Runs after parsing input, before setting value. */
      transform?: (value: InferOutput<T>, ctx: Ctx, ext: ExternalCtx) => InferOutput<T>;
    },
    deps: Deps
  ): DataGraph<
    AppendKey<Ctx, { [P in K]: InferOutput<T> }>,
    ExternalCtx,
    M extends undefined ? CtxMeta : Prettify<CtxMeta & { [P in K]: M }>,
    Prettify<CtxValues & { [P in K]: InferOutput<T> }>
  >;

  /**
   * Add a conditional node with a factory function (has `when` boolean - optional in type).
   *
   * Note: Ctx uses optional `[P in K]?` because the node may not exist (when=false).
   * But CtxValues uses required `[P in K]` because when the node IS active (Controller renders),
   * the value is always defined (uses defaultValue or input value).
   */
  node<const K extends string, const Deps extends readonly string[], T extends AnyZodSchema, M>(
    key: K,
    factory: (
      ctx: Ctx,
      ext: ExternalCtx,
      actions: GraphActions<Ctx>
    ) => {
      input?: AnyZodSchema;
      output: T;
      defaultValue?: InferOutput<T>;
      /** Static meta object, or function that computes meta from context and current value */
      meta?: M | ((ctx: Ctx, ext: ExternalCtx, value: InferOutput<T>) => M);
      when: boolean;
      /** Transform value when dependencies change. Runs after parsing input, before setting value. */
      transform?: (value: InferOutput<T>, ctx: Ctx, ext: ExternalCtx) => InferOutput<T>;
    },
    deps: Deps
  ): DataGraph<
    AppendKey<Ctx, { [P in K]?: InferOutput<T> }>,
    ExternalCtx,
    M extends undefined ? CtxMeta : Prettify<CtxMeta & { [P in K]: M }>,
    Prettify<CtxValues & { [P in K]: InferOutput<T> }>
  >;

  // `deps` entries are ctx keys by default. To re-run a node when an external
  // context key changes, list it prefixed with `ext:` (e.g. `['ecosystem',
  // 'ext:limits']`). `setExt` seeds `ext:<key>` tokens for changed top-level
  // ext keys, which match here via the same dep-changed check used for ctx keys.
  // The `ext:` prefix is collision-safe since ctx keys are plain identifiers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node(key: string, arg1: any, deps?: readonly string[]) {
    const isFactory = typeof arg1 === 'function';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const factory = isFactory ? arg1 : () => arg1;
    const nodeDeps = isFactory ? (Array.isArray(deps) ? deps : []) : [];

    this.addEntry({ kind: 'node', key, factory, deps: nodeDeps });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this as any;
  }

  /**
   * Add a discriminator that switches between sub-graphs based on a discriminator key.
   */
  discriminator<
    const DiscKey extends keyof Ctx & string,
    const Branches extends BranchesRecord<Ctx, ExternalCtx>
  >(
    key: DiscKey,
    branchesOrFactory: Branches | ((ctx: Ctx, ext: ExternalCtx) => Branches)
  ): DataGraph<
    BuildDiscriminatedUnion<Ctx, DiscKey, Branches>,
    ExternalCtx,
    BuildDiscriminatedMetaUnion<CtxMeta, Branches>,
    BuildDiscriminatedValuesUnion<CtxValues, Branches>
  > {
    const factory =
      typeof branchesOrFactory === 'function' ? branchesOrFactory : () => branchesOrFactory;
    this.addEntry({
      kind: 'discriminator',
      discriminatorKey: key,
      factory,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this as any;
  }

  /**
   * Add a grouped discriminator that collapses multiple values into fewer type branches.
   *
   * This is useful when many discriminator values share the same subgraph - instead of
   * creating a type union member for each value, this creates ONE member per group.
   *
   * @example
   * ```typescript
   * // Before: 12 branches → 12 type union members
   * .discriminator('workflow', {
   *   txt2img: ecosystemGraph,
   *   'txt2img:draft': ecosystemGraph,
   *   // ... 10 more pointing to ecosystemGraph
   *   'vid2vid:interpolate': videoInterpolationGraph,
   * })
   *
   * // After: 5 groups → 5 type union members
   * .groupedDiscriminator('workflow', [
   *   { values: ['txt2img', 'txt2img:draft', ...] as const, graph: ecosystemGraph },
   *   { values: ['vid2vid:interpolate'] as const, graph: videoInterpolationGraph },
   *   // ...
   * ])
   * ```
   */
  groupedDiscriminator<
    const DiscKey extends keyof Ctx & string,
    const Groups extends GroupedBranchesArray<Ctx, ExternalCtx>
  >(
    key: DiscKey,
    groups: Groups
  ): DataGraph<
    BuildGroupedDiscriminatedUnion<Ctx, DiscKey, Groups>,
    ExternalCtx,
    BuildGroupedMetaUnion<CtxMeta, Groups>,
    BuildGroupedValuesUnion<CtxValues, Groups>
  > {
    // Convert grouped branches to a flat BranchesRecord for runtime
    // Also build a value→group map so the discriminator can tell when two values
    // belong to the same group (avoiding unnecessary branch deactivation/reactivation)
    const branches: BranchesRecord = {};
    const groupOf = new Map<string, string>();
    for (const group of groups) {
      const groupName = group.values[0] as string;
      for (const value of group.values) {
        branches[value] = group.graph;
        groupOf.set(value, groupName);
      }
    }

    this.addEntry({
      kind: 'discriminator',
      discriminatorKey: key,
      factory: () => branches,
      groupOf,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this as any;
  }

  /**
   * Merge another graph's nodes into this graph.
   *
   * Can be called with:
   * 1. A graph directly: `.merge(otherGraph)` - entries are added as-is
   * 2. A factory with deps: `.merge((ctx, ext) => createGraph(...), ['dep1', 'dep2'])`
   *    - Each entry's deps will be combined with the merge deps
   *    - Allows dynamic graph creation based on context
   *    - Deps are ctx keys, or `ext:<key>` to re-run on an external-context
   *      change (same convention `node` documents)
   */
  merge<
    ChildCtx extends Record<string, unknown>,
    ChildExternal extends Record<string, unknown>,
    ChildMeta extends Record<string, unknown>,
    ChildValues extends Record<string, unknown>
  >(
    graph: DataGraph<ChildCtx, ChildExternal, ChildMeta, ChildValues>
  ): ExternalCtx extends ChildExternal
    ? DataGraph<
        MergeDistributive<Ctx, ChildCtx>,
        ExternalCtx,
        Prettify<CtxMeta & ChildMeta>,
        Prettify<CtxValues & ChildValues>
      >
    : never;

  merge<
    ChildCtx extends Record<string, unknown>,
    ChildExternal extends Record<string, unknown>,
    ChildMeta extends Record<string, unknown>,
    ChildValues extends Record<string, unknown>,
    const Deps extends readonly ((keyof Ctx & string) | `ext:${string}`)[]
  >(
    factory: (
      ctx: Ctx,
      ext: ExternalCtx
    ) => DataGraph<ChildCtx, ChildExternal, ChildMeta, ChildValues>,
    deps: Deps
  ): ExternalCtx extends ChildExternal
    ? DataGraph<
        MergeDistributive<Ctx, ChildCtx>,
        ExternalCtx,
        Prettify<CtxMeta & ChildMeta>,
        Prettify<CtxValues & ChildValues>
      >
    : never;

  // prettier-ignore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  merge(graphOrFactory: DataGraph<any, any, any, any> | ((ctx: Ctx, ext: ExternalCtx) => DataGraph<any, any, any, any>), deps?: readonly string[]): any {
    if (typeof graphOrFactory === 'function') {
      // Factory mode: call factory once to get graph structure, then add entries with combined deps
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const templateGraph = graphOrFactory({} as any, {} as any);
      const mergeDeps = deps ?? [];

      for (const entry of templateGraph.entries) {
        switch (entry.kind) {
          case 'node': {
            // Wrap the node factory to re-call merge factory with current context
            const originalFactory = entry.factory;
            this.addEntry({
              kind: 'node',
              key: entry.key,
              factory: (ctx, ext, actions) => {
                const freshGraph = graphOrFactory(ctx, ext);
                const freshEntry = freshGraph.entries.find(
                  (e): e is typeof entry => e.kind === 'node' && e.key === entry.key
                );
                return freshEntry ? freshEntry.factory(ctx, ext, actions) : originalFactory(ctx, ext, actions);
              },
              deps: [...new Set([...entry.deps, ...mergeDeps])],
            });
            break;
          }
          case 'computed': {
            // Wrap the computed function to re-call merge factory with current context
            // This ensures computed functions use fresh closure values (e.g., versions)
            const originalCompute = entry.compute;
            this.addEntry({
              kind: 'computed',
              key: entry.key,
              compute: (ctx, ext) => {
                const freshGraph = graphOrFactory(ctx, ext);
                const freshEntry = freshGraph.entries.find(
                  (e): e is typeof entry => e.kind === 'computed' && e.key === entry.key
                );
                return freshEntry ? freshEntry.compute(ctx, ext) : originalCompute(ctx, ext);
              },
              deps: [...new Set([...entry.deps, ...mergeDeps])],
            });
            break;
          }
          case 'effect': {
            // Wrap the effect function to re-call merge factory with current context
            const originalRun = entry.run;
            this.addEntry({
              kind: 'effect',
              run: (ctx, ext, set) => {
                const freshGraph = graphOrFactory(ctx, ext);
                const freshEntry = freshGraph.entries.find(
                  (e): e is typeof entry => e.kind === 'effect' && e.run === originalRun
                );
                return freshEntry ? freshEntry.run(ctx, ext, set) : originalRun(ctx, ext, set);
              },
              deps: [...new Set([...entry.deps, ...mergeDeps])],
            });
            break;
          }
          case 'discriminator':
            this.addEntry(entry);
            break;
        }
      }
    } else {
      // Direct graph mode: copy entries as-is
      for (const entry of graphOrFactory.entries) {
        this.addEntry(entry);
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this as any;
  }

  effect<const Deps extends readonly string[]>(fn: EffectFn<Ctx, ExternalCtx>, deps: Deps): this {
    this.addEntry({ kind: 'effect', run: fn, deps: deps as readonly string[] });
    return this;
  }

  /**
   * Add a computed (derived) value that is calculated from other nodes.
   * Deps can reference any key that exists in any branch (uses CtxValues which accumulates all keys).
   */
  computed<const K extends string, T, const Deps extends readonly (keyof CtxValues & string)[]>(
    key: K,
    compute: (ctx: Ctx, ext: ExternalCtx) => T,
    deps: Deps
  ): DataGraph<
    AppendKey<Ctx, { [P in K]: T }>,
    ExternalCtx,
    CtxMeta,
    Prettify<CtxValues & { [P in K]: T }>
  > {
    this.addEntry({
      kind: 'computed',
      key,
      compute,
      deps: deps as readonly string[],
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this as any;
  }

  /**
   * Set up internal state for evaluation (shared by init and validate).
   * This prepares the graph for evaluation without handling storage or watchers.
   */
  private _setup(externalCtx: ExternalCtx): void {
    this._ext = externalCtx;
    this._ctx = {} as Ctx;
    this.activeDiscriminators.clear();
    this.activeEntries = [];

    // Initialize activeEntries from template entries with 'root' source
    for (const entry of this.entries) {
      this.activeEntries.push({ ...entry, source: 'root' });
    }
  }

  /**
   * Initialize the graph with optional input values and external context.
   *
   * @param options.debug - Enable debug logging
   * @param options.skipStorage - Skip loading values from storage adapter
   */
  init(
    input: Partial<Ctx> = {},
    externalCtx: ExternalCtx = {} as ExternalCtx,
    options: { debug?: boolean; skipStorage?: boolean } = {}
  ): Ctx {
    this._debug = options.debug ?? false;
    this._initialized = true;

    // Use common setup
    this._setup(externalCtx);

    let mergedInput = input;
    if (this.storageAdapter && !options.skipStorage) {
      const storageValues = this.storageAdapter.getValues();
      mergedInput = { ...storageValues, ...input };
      this.storageAdapter.onBeforeEvaluate?.();
    } else if (options.skipStorage) {
      this.valueProvider = undefined;
    }

    const changed = this._evaluate(mergedInput);
    this._updateAllMeta();
    this._notifyChanges(changed);

    if (this.storageAdapter) {
      this.storageAdapter.onInit();
    }

    return this._ctx;
  }

  set(values: Partial<Ctx>): Ctx {
    if (!this._initialized) throw new Error('Pipeline not initialized. Call init() first.');

    const changed = this._evaluate(values);
    this._updateAllMeta();
    this._notifyChanges(changed);

    if (this.storageAdapter) {
      this.storageAdapter.onSet(values, this._ctx);
    }
    return this._ctx;
  }

  setExt(values: Partial<ExternalCtx>): Ctx {
    if (!this._initialized) throw new Error('Pipeline not initialized. Call init() first.');
    // Diff per top-level ext key so we only re-run nodes whose ext dep actually
    // changed. Callers (e.g. the React provider) typically push the entire
    // external context object on every change, so without this diff every
    // `ext:`-dep node would re-evaluate on every push. Nodes opt into ext
    // reactivity by listing `ext:<key>` in their `deps` array.
    const changedExtKeys: string[] = [];
    for (const key of Object.keys(values)) {
      if (
        !isEqual(
          (this._ext as Record<string, unknown>)[key],
          (values as Record<string, unknown>)[key]
        )
      ) {
        changedExtKeys.push(`ext:${key}`);
      }
    }
    Object.assign(this._ext, values);
    const changed = this._evaluate({}, changedExtKeys);
    this._updateAllMeta();
    this._notifyChanges(changed);
    return this._ctx;
  }

  reset(options: { exclude?: ((keyof Ctx | keyof CtxMeta) & string)[] } = {}): Ctx {
    const { exclude = [] } = options;

    const preservedValues: Record<string, unknown> = {};
    for (const key of exclude) {
      if (key in this._ctx) {
        preservedValues[key] = (this._ctx as Record<string, unknown>)[key];
      } else if (this.valueProvider) {
        // For excluded keys not in ctx (inactive nodes, e.g. when: false),
        // read from the valueProvider (backed by storage) so they survive the reset
        const stored = this.valueProvider(key, this._ctx);
        if (stored !== undefined) {
          preservedValues[key] = stored;
        }
      }
    }

    const result = this.init(preservedValues as Partial<Ctx>, this._ext, {
      debug: this._debug,
      skipStorage: true,
    });
    if (this.storageAdapter) {
      // Persist reset defaults to localStorage BEFORE reinstating the valueProvider.
      // Without this, the next set() would re-read stale pre-reset values from storage.
      this.storageAdapter.onSet(this._ctx, this._ctx);
      this.storageAdapter.onBeforeEvaluate?.();
    }
    return result;
  }

  clone(): DataGraph<Ctx, ExternalCtx, CtxMeta, CtxValues> {
    const cloned = new DataGraph<Ctx, ExternalCtx, CtxMeta, CtxValues>();
    cloned.entries = this.entries.map((e) => ({ ...e }));
    cloned._debug = this._debug;
    this.computedNodes.forEach((key) => cloned.computedNodes.add(key));
    this.lazyBranchCache.forEach((graph, key) => cloned.lazyBranchCache.set(key, graph));
    cloned.valueProvider = this.valueProvider;
    // Copy scope dependencies
    this.scopeDependencies.forEach((deps, key) => {
      cloned.scopeDependencies.set(key, new Set(deps));
    });
    return cloned;
  }

  /** Get all active node keys in the current context */
  getNodeKeys(): string[] {
    return Object.keys(this.rootGraph._ctx).filter((k) => !this.rootGraph.computedNodes.has(k));
  }

  /** Get node keys defined by this graph's template entries */
  getOwnNodeKeys(): string[] {
    const keys: string[] = [];
    for (const entry of this.entries) {
      if (entry.kind === 'node') {
        keys.push(entry.key);
      }
    }
    return keys;
  }

  /** Get all active computed keys (includes subgraph computed keys) */
  getComputedKeys(): string[] {
    return Array.from(this.rootGraph.computedNodes);
  }

  /** Get all possible keys across all discriminator branches */
  getAllPossibleKeys(): string[] {
    const keys = new Set<string>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const collectKeys = (graph: DataGraph<any, any, any, any>) => {
      for (const entry of graph.entries) {
        if (entry.kind === 'node') {
          keys.add(entry.key);
        } else if (entry.kind === 'computed') {
          keys.add(entry.key);
        } else if (entry.kind === 'discriminator') {
          const branches = entry.factory({}, {});
          for (const branch of Object.values(branches)) {
            if (branch instanceof DataGraph) {
              collectKeys(branch);
            } else if (typeof branch === 'function') {
              try {
                const lazyGraph = branch({}, {});
                if (lazyGraph instanceof DataGraph) {
                  collectKeys(lazyGraph);
                }
              } catch {
                // If lazy factory fails, skip it
              }
            }
          }
        }
      }
    };

    collectKeys(this);
    return Array.from(keys);
  }

  // ===========================================================================
  // Branch Key Introspection
  // ===========================================================================

  /**
   * Check if a graph has a node or computed key as a direct entry
   * (not behind a discriminator).
   *
   * When context is provided, tries to evaluate the node factory and check
   * the `when` condition. If `when` is explicitly `false`, the node is
   * considered absent. Falls back to treating the node as present if the
   * factory throws (e.g., needs more context than discriminator values).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static hasDirectKey(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    graph: DataGraph<any, any, any, any>,
    nodeKey: string,
    context?: Record<string, unknown>
  ): boolean {
    for (const entry of graph.entries) {
      if ((entry.kind === 'node' || entry.kind === 'computed') && entry.key === nodeKey) {
        // If we have context and it's a node with a factory, check `when`
        if (context && entry.kind === 'node') {
          try {
            const result = entry.factory(context, {}, {});
            if (result && 'when' in result && result.when === false) {
              return false;
            }
          } catch {
            // Factory needs more context than we have; assume node is active
          }
        }
        return true;
      }
    }
    return false;
  }

  /**
   * Resolve a branch definition to a DataGraph instance.
   * Handles both direct DataGraph instances and lazy factories.
   */
  private static resolveBranch(branch: BranchDefinition): BranchGraph | null {
    if (branch instanceof DataGraph) return branch;
    if (typeof branch === 'function') {
      try {
        const lazyGraph = branch({}, {});
        if (lazyGraph instanceof DataGraph) return lazyGraph;
      } catch {
        // If lazy factory fails, skip it
      }
    }
    return null;
  }

  /**
   * Resolve the default value for a discriminator by evaluating the corresponding
   * node factory with the given context. Returns undefined if no node is found
   * or the factory throws.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static resolveDiscriminatorDefault(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    graph: DataGraph<any, any, any, any>,
    discriminatorKey: string,
    context: Record<string, unknown>
  ): string | undefined {
    for (const entry of graph.entries) {
      if (entry.kind === 'node' && entry.key === discriminatorKey) {
        try {
          const result = entry.factory(context, {}, {});
          if (result && result.defaultValue !== undefined) {
            return String(result.defaultValue);
          }
        } catch {
          // Factory needs more context than we have
        }
        break;
      }
    }
    return undefined;
  }

  /**
   * Find which branches of a discriminator contain a specific node key,
   * drilling through nested discriminators along a specified search path.
   *
   * The `discriminators` array defines the search path:
   * 1. The first discriminator is looked up in this graph's entries
   * 2. For each branch, checks if `nodeKey` exists directly (node/computed entry)
   * 3. If found directly → `{ kind: 'direct' }`, stop drilling
   * 4. If not found and there are remaining discriminators → recurse into the
   *    sub-graph with the remaining discriminators
   * 5. Continue until the node is found or discriminators are exhausted
   *
   * When context is provided, it controls which branches are visited:
   * - **Pinned**: If the context contains a value for the current discriminator,
   *   only that branch is visited (e.g., `{ workflow: 'image:draft' }`)
   * - **Resolved**: If context exists but doesn't pin the current discriminator,
   *   the discriminator's node factory is evaluated with the accumulated context
   *   to resolve a default value, and only that branch is visited
   * - **Discovery**: If no context is provided, all branches are iterated
   *
   * Context is also passed to node factories to evaluate `when` conditions.
   *
   * @example
   * ```ts
   * // Discovery: find all workflows that have an 'images' node
   * generationGraph.findKeyInBranches(['workflow', 'ecosystem'], 'images')
   *
   * // Context-aware: check if image:draft has an 'images' node
   * // Resolves ecosystem to default (SD1), evaluates when → false
   * generationGraph.findKeyInBranches(['workflow', 'ecosystem'], 'images',
   *   { workflow: 'image:draft' })
   * // → [] (images has when:false in SD family for draft)
   * ```
   */
  findKeyInBranches(
    discriminators: string[],
    nodeKey: string,
    _context?: Record<string, unknown>
  ): BranchKeyInfo[] {
    if (discriminators.length === 0) return [];

    const context = _context ?? {};
    const [currentDisc, ...remainingDiscs] = discriminators;

    // Find the discriminator entry matching the current discriminator key
    const discEntry = this.entries.find(
      (e): e is Extract<GraphEntry, { kind: 'discriminator' }> =>
        e.kind === 'discriminator' && e.discriminatorKey === currentDisc
    );
    if (!discEntry) return [];

    const branches = discEntry.factory({}, {});

    // Determine which branch values to visit
    let valuesToVisit: string[];

    if (currentDisc in context) {
      // Pinned: context specifies this discriminator's value
      const pinned = String(context[currentDisc]);
      valuesToVisit = pinned in branches ? [pinned] : [];
    } else if (_context) {
      // Resolved: context exists but doesn't pin this discriminator.
      // Evaluate the discriminator's node factory to get the default value.
      const defaultValue = DataGraph.resolveDiscriminatorDefault(this, currentDisc, context);
      if (defaultValue && defaultValue in branches) {
        valuesToVisit = [defaultValue];
      } else {
        // Couldn't resolve a default — fall back to all branches
        valuesToVisit = Object.keys(branches);
      }
    } else {
      // Discovery: no context, iterate all branches
      valuesToVisit = Object.keys(branches);
    }

    const results: BranchKeyInfo[] = [];

    for (const value of valuesToVisit) {
      const branchDef = branches[value];
      const graph = DataGraph.resolveBranch(branchDef);
      if (!graph) continue;

      // Build context with the current discriminator value
      const branchContext = { ...context, [currentDisc]: value };

      // Check if the key exists directly in this branch's graph
      if (DataGraph.hasDirectKey(graph, nodeKey, branchContext)) {
        results.push({ value, match: { kind: 'direct' } });
        continue;
      }

      // If there are more discriminators, recurse into the sub-graph
      if (remainingDiscs.length > 0) {
        const nested = graph.findKeyInBranches(remainingDiscs, nodeKey, branchContext);
        if (nested.length > 0) {
          results.push({
            value,
            match: { kind: 'nested', discriminator: remainingDiscs[0], branches: nested },
          });
          continue;
        }
      }
    }

    return results;
  }

  // ===========================================================================
  // Branch Management - Add/Remove entries when discriminators change
  // ===========================================================================

  /**
   * Add entries from a subgraph to the active entries list.
   * Recursively handles nested discriminators.
   *
   * @param discriminatorKey - The discriminator key (e.g., 'workflow', 'input')
   * @param branchName - The branch being activated (e.g., 'txt2img', 'image')
   * @param branchGraph - The subgraph to activate
   * @param insertAfterIndex - Index to insert entries after
   * @param parentSource - Parent discriminator's source for nested discriminators
   */
  private activateBranch(
    discriminatorKey: string,
    branchName: string,
    branchGraph: BranchGraph,
    insertAfterIndex: number,
    parentSource?: string
  ): { entryKeys: Set<string>; insertedCount: number } {
    // Build source path: for nested discriminators, include parent path
    // e.g., 'workflow:txt2img/input:image' for input discriminator inside workflow branch
    // Root discriminators (parentSource === 'root') don't include 'root' in the path
    const source =
      parentSource && parentSource !== 'root'
        ? `${parentSource}/${discriminatorKey}:${branchName}`
        : `${discriminatorKey}:${branchName}`;
    const entryKeys = new Set<string>();
    const newEntries: ActiveEntry[] = [];

    for (const entry of branchGraph.entries) {
      const activeEntry: ActiveEntry = { ...entry, source };
      newEntries.push(activeEntry);

      if (entry.kind === 'node' || entry.kind === 'computed') {
        entryKeys.add(entry.key);
        if (entry.kind === 'computed') {
          this.computedNodes.add(entry.key);
        }
      }
    }

    // Insert new entries after the discriminator entry
    this.activeEntries.splice(insertAfterIndex + 1, 0, ...newEntries);

    return { entryKeys, insertedCount: newEntries.length };
  }

  /**
   * Remove entries that were added by a specific discriminator branch.
   * Returns the keys that were removed (for change tracking).
   */
  private deactivateBranch(
    discriminatorKey: string,
    branchName: string,
    parentSource?: string
  ): Set<string> {
    const source =
      parentSource && parentSource !== 'root'
        ? `${parentSource}/${discriminatorKey}:${branchName}`
        : `${discriminatorKey}:${branchName}`;
    const sourcePrefix = `${source}/`;
    const removedKeys = new Set<string>();

    // Find all entries to remove (direct and nested)
    const entriesToRemove: ActiveEntry[] = [];
    for (const entry of this.activeEntries) {
      if (entry.source === source || entry.source.startsWith(sourcePrefix)) {
        entriesToRemove.push(entry);
      }
    }

    // Clean up state for removed entries
    for (const entry of entriesToRemove) {
      if (entry.kind === 'node' || entry.kind === 'computed') {
        delete (this._ctx as Record<string, unknown>)[entry.key];
        this.nodeMeta.delete(entry.key);
        this.nodeDefs.delete(entry.key);
        this.nodeErrors.delete(entry.key);
        if (entry.kind === 'computed') {
          this.computedNodes.delete(entry.key);
        }
        removedKeys.add(entry.key);
      }
      if (entry.kind === 'discriminator') {
        this.activeDiscriminators.delete(`${entry.source}|${entry.discriminatorKey}`);
      }
    }

    // Remove entries from activeEntries
    this.activeEntries = this.activeEntries.filter(
      (e) => e.source !== source && !e.source.startsWith(sourcePrefix)
    );

    return removedKeys;
  }

  // ===========================================================================
  // Evaluation Loop
  // ===========================================================================

  /**
   * Evaluate the graph and return the set of keys that changed.
   *
   * @param inputValues - Node values being set this pass (ctx keys)
   * @param additionalChangedKeys - Extra keys to seed into `changed` so nodes
   *   that list them in `deps` re-run. Used by `setExt` to inject `ext:<key>`
   *   tokens for changed external-context keys (see the `ext:` dep convention).
   */
  private _evaluate(
    inputValues: Partial<Ctx> = {},
    additionalChangedKeys: readonly string[] = []
  ): Set<string> {
    const log = this._debug ? console.log.bind(console) : () => undefined;

    const changed = new Set<string>([...Object.keys(inputValues), ...additionalChangedKeys]);

    let iterations = 0;
    let currentIndex = 0;

    // Build key-to-index map for rewinding
    const rebuildKeyToIndex = () => {
      const map = new Map<string, number>();
      for (let i = 0; i < this.activeEntries.length; i++) {
        const entry = this.activeEntries[i];
        if (entry.kind === 'node' || entry.kind === 'computed') {
          map.set(entry.key, i);
        }
      }
      return map;
    };

    let keyToIndex = rebuildKeyToIndex();

    const effectSet = (key: string, value: unknown) => {
      const def = this.nodeDefs.get(key);
      if (!def) throw new Error(`Unknown node "${key}"`);

      const schema = def.input ?? def.output;
      const next = schema.parse(value);

      if (!isEqual((this._ctx as Record<string, unknown>)[key], next)) {
        (this._ctx as Record<string, unknown>)[key] = next;
        changed.add(key);

        // Rewind to the node if it's earlier in the list
        const nodeIndex = keyToIndex.get(key);
        if (nodeIndex !== undefined && nodeIndex < currentIndex) {
          currentIndex = nodeIndex;
        }
      }
    };

    while (currentIndex < this.activeEntries.length) {
      if (++iterations > 1000) throw new Error('Effect loop detected');

      const entry = this.activeEntries[currentIndex];

      if (entry.kind === 'node') {
        // Process if: no deps, a dep changed, OR this node's value is being set directly
        const isDirectUpdate = entry.key in inputValues;
        // Check if any scope dependency changed
        const isScopeDependencyChange = Array.from(this.scopeDependencies.entries()).some(
          ([scopeKey, dependentKeys]) => changed.has(scopeKey) && dependentKeys.has(entry.key)
        );
        // Check if this node was just activated from a branch switch
        // (its key is in changed, meaning activateBranch just added it)
        const isFromBranchActivation = changed.has(entry.key);
        const shouldProcess =
          isDirectUpdate ||
          isScopeDependencyChange ||
          isFromBranchActivation ||
          entry.deps.length === 0 ||
          entry.deps.some((dep) => changed.has(dep));

        if (!shouldProcess) {
          currentIndex++;
          continue;
        }

        const actions: GraphActions<Ctx> = {
          set: (values) => this.set(values),
          reset: () => this.reset(),
        };
        const def = entry.factory(this._ctx, this._ext, actions);
        this.nodeDefs.set(entry.key, def);

        if (def.when === false) {
          if (entry.key in this._ctx) {
            delete (this._ctx as Record<string, unknown>)[entry.key];
            this.nodeMeta.delete(entry.key);
            this.nodeDefs.delete(entry.key);
            changed.add(entry.key);
          }
          currentIndex++;
          continue;
        }

        let inputValue: unknown;
        if (entry.key in inputValues) {
          inputValue = inputValues[entry.key as keyof Ctx];
        } else if (this.valueProvider) {
          const providedValue = this.valueProvider(entry.key, this._ctx);
          if (providedValue !== undefined) {
            inputValue = providedValue;
          } else if (isScopeDependencyChange) {
            inputValue = undefined;
          } else if (entry.key in this._ctx) {
            inputValue = this._ctx[entry.key as keyof Ctx];
          }
        } else if (entry.key in this._ctx) {
          inputValue = this._ctx[entry.key as keyof Ctx];
        }

        const raw = inputValue !== undefined ? inputValue : def.defaultValue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let next: any;

        // Use input schema if available (allows transforms)
        const schema = def.input ?? def.output;
        try {
          next = schema.parse(raw);
          if (next === undefined && def.defaultValue !== undefined) {
            next = def.defaultValue;
          }
        } catch {
          next = def.defaultValue ?? def.output.parse(def.defaultValue);
        }

        // Apply transform when deps changed (not on direct input)
        // Transform allows updating value based on context changes
        const depsChanged = entry.deps.some((dep) => changed.has(dep));
        if (def.transform && depsChanged && !isDirectUpdate) {
          next = def.transform(next, this._ctx, this._ext);
        }

        const keyExists = entry.key in this._ctx;
        const valueChanged = !isEqual(this._ctx[entry.key as keyof Ctx], next);
        if (!keyExists || valueChanged) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this._ctx as any)[entry.key] = next;
          changed.add(entry.key);
        }
      } else if (entry.kind === 'computed') {
        // Check if this node was just activated from a branch switch
        const isFromBranchActivation = changed.has(entry.key);
        const shouldProcess =
          isFromBranchActivation ||
          entry.deps.length === 0 ||
          entry.deps.some((dep) => changed.has(dep));

        if (!shouldProcess) {
          currentIndex++;
          continue;
        }

        const keyExists = entry.key in this._ctx;
        const next = entry.compute(this._ctx, this._ext);
        const valueChanged = !isEqual(this._ctx[entry.key as keyof Ctx], next);
        if (!keyExists || valueChanged) {
          (this._ctx as Record<string, unknown>)[entry.key] = next;
          changed.add(entry.key);
        }
      } else if (entry.kind === 'discriminator') {
        // Scope discriminator tracking by source path to support nested discriminators
        // with the same key (e.g., parent and child both using 'ecosystem')
        const discId = `${entry.source}|${entry.discriminatorKey}`;
        const current = this.activeDiscriminators.get(discId);

        // Only process if discriminator key changed OR no branch is active yet
        if (!changed.has(entry.discriminatorKey) && current) {
          currentIndex++;
          continue;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const discriminatorValue = (this._ctx as any)[entry.discriminatorKey];
        const branches = entry.factory(this._ctx, this._ext);
        const targetBranch = discriminatorValue as string;
        const branchDef = targetBranch ? branches[targetBranch] : undefined;

        // For groupedDiscriminator, resolve the effective branch name (group name).
        // Values in the same group share the same graph, so switching between them
        // should NOT trigger branch deactivation/reactivation.
        const effectiveBranch = entry.groupOf?.get(targetBranch) ?? targetBranch;

        if (!branchDef) {
          if (current) {
            log(`  🔀 discriminator: no matching branch for "${targetBranch}", cleaning up`);
            const removedKeys = this.deactivateBranch(
              entry.discriminatorKey,
              current.branch,
              entry.source
            );
            for (const key of removedKeys) changed.add(key);
            this.activeDiscriminators.delete(discId);
            keyToIndex = rebuildKeyToIndex();
          }
          currentIndex++;
          continue;
        }

        const needsSwitch = !current || current.branch !== effectiveBranch;
        if (needsSwitch) {
          log(`  🔀 discriminator: switching to branch "${effectiveBranch}"`);

          // Deactivate old branch
          if (current) {
            const removedKeys = this.deactivateBranch(
              entry.discriminatorKey,
              current.branch,
              entry.source
            );
            for (const key of removedKeys) changed.add(key);
          }

          // Get or create branch graph (cache by effective branch, not raw value)
          const cacheKey = `${entry.discriminatorKey}:${effectiveBranch}`;
          let branchGraph: BranchGraph;
          if (typeof branchDef === 'function') {
            const cached = this.lazyBranchCache.get(cacheKey);
            if (cached) {
              branchGraph = cached;
            } else {
              branchGraph = branchDef(this._ctx, this._ext);
              this.lazyBranchCache.set(cacheKey, branchGraph);
              log(`  📦 lazy branch "${effectiveBranch}" instantiated and cached`);
            }
          } else {
            branchGraph = branchDef;
          }

          // Activate new branch - insert entries after this discriminator
          // Use effective branch name so source paths are consistent within a group
          const { entryKeys } = this.activateBranch(
            entry.discriminatorKey,
            effectiveBranch,
            branchGraph,
            currentIndex,
            entry.source // Parent source for nested path tracking
          );

          // Track active branch using the effective (group) name
          this.activeDiscriminators.set(discId, {
            branch: effectiveBranch,
            entryKeys,
          });

          // Rebuild key-to-index map since entries changed
          keyToIndex = rebuildKeyToIndex();

          // Mark all new entry keys as needing processing
          for (const key of entryKeys) {
            changed.add(key);
          }

          // Fall through to increment currentIndex, which will point to the first inserted entry
        }
      } else if (entry.kind === 'effect') {
        const depsChanged = entry.deps.some((dep) => changed.has(dep));
        if (!depsChanged) {
          currentIndex++;
          continue;
        }

        entry.run(this._ctx, this._ext, effectSet);
      }

      currentIndex++;
    }

    return changed;
  }

  /** Notify watchers for changed keys and global watchers. */
  private _notifyChanges(changed: Set<string>): void {
    if (changed.size > 0) {
      // Invalidate full context snapshot cache
      this._ctxSnapshot = null;
      // Invalidate full output snapshot cache
      this._outputSnapshot = null;
    }
    for (const key of changed) {
      this.notifyNodeWatchers(key);
    }
    for (const callback of this.globalWatchers) {
      callback();
    }
  }
}

// ============================================================================
// Helper Types for Extracting Graph Types
// ============================================================================

/**
 * Extract all types from a DataGraph definition.
 * Use this to get typed hooks without manual type parameters.
 *
 * @example
 * ```tsx
 * const graph = new DataGraph<{}, { maxSteps: number }>()
 *   .node('steps', { output: z.number(), defaultValue: 20, meta: { min: 1, max: 50 } });
 *
 * type GraphTypes = InferDataGraph<typeof graph>;
 * // GraphTypes.Ctx = { steps: number }
 * // GraphTypes.ExternalCtx = { maxSteps: number }
 * // GraphTypes.Meta = { steps: { min: number; max: number } }
 *
 * // In components:
 * const graph = useGraph<GraphTypes.Ctx, GraphTypes.ExternalCtx, GraphTypes.Meta>();
 * ```
 */
export type InferDataGraph<G> = G extends DataGraph<
  infer Ctx,
  infer ExternalCtx,
  infer CtxMeta,
  infer CtxValues
>
  ? {
      /**
       * The context type containing all node values (discriminated union).
       *
       * Branches are distributively prettified at extraction time — each branch
       * is flattened from `(branch & {n1} & {n2} & ...)` into a plain object so
       * downstream property access doesn't walk the intersection chain.
       */
      Ctx: DistribPrettify<Ctx>;
      /** External context passed from outside the graph */
      ExternalCtx: ExternalCtx;
      /** Per-node meta type mapping (intersection of all branch metas) */
      Meta: CtxMeta;
      /** All possible value types (intersection of all branch contexts) */
      Values: CtxValues;
      /** The full graph type */
      Graph: DataGraph<Ctx, ExternalCtx, CtxMeta, CtxValues>;
    }
  : never;

// Export type utilities
export type {
  InferGraphContext,
  InferGraphMeta,
  BuildDiscriminatedUnion,
  BuildGroupedDiscriminatedUnion,
  GroupedBranch,
  GroupedBranchesArray,
  InferOutput,
  AnyZodSchema,
  GraphActions,
};
